import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cashflow-596/artifacts/ipba3h4w_Gazeal%20Advance%20logo.png";

const FooterReveal = () => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const revealRef = useRef(null);
  const footerHeight = 600; // Height of the revealed footer

  useEffect(() => {
    const handleScroll = () => {
      if (!revealRef.current) return;
      
      const rect = revealRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate how much of the reveal section is in view
      const visibleAmount = windowHeight - rect.top;
      const progress = Math.min(Math.max(visibleAmount / footerHeight, 0), 1);
      
      setScrollProgress(progress);
      setIsRevealed(progress > 0.1);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={revealRef} className="relative" style={{ height: `${footerHeight}px` }}>
      {/* Fixed Footer underneath - only visible when scrolled to reveal section */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-black"
        style={{ 
          height: `${footerHeight}px`,
          zIndex: 1,
          pointerEvents: scrollProgress > 0.1 ? 'auto' : 'none',
        }}
      >
        {/* Neon glow effect at top */}
        <div 
          className="absolute top-0 left-0 right-0 h-2 bg-[#59d862]"
          style={{
            boxShadow: '0 0 60px 20px rgba(89, 216, 98, 0.6), 0 0 100px 40px rgba(89, 216, 98, 0.3)',
            opacity: scrollProgress,
          }}
        />
        
        {/* Large Logo */}
        <div className="flex items-center justify-center h-3/5 pt-8">
          <img
            src={LOGO_URL}
            alt="Gazeal Advance"
            className="h-24 md:h-32 lg:h-40 w-auto animate-glow-pulse"
            style={{
              opacity: scrollProgress,
              transform: `scale(${0.8 + scrollProgress * 0.2})`,
              transition: 'transform 0.3s ease-out',
            }}
          />
        </div>

        {/* Footer Content */}
        <div 
          className="max-w-[1400px] mx-auto px-6 lg:px-12"
          style={{ opacity: scrollProgress }}
        >
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Links */}
              <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
                <a href="/#how-it-works" className="text-white/60 hover:text-[#59d862] transition-colors text-sm flex items-center gap-1">
                  How It Works <ArrowUpRight className="w-3 h-3" />
                </a>
                <a href="/#why-gazeal" className="text-white/60 hover:text-[#59d862] transition-colors text-sm flex items-center gap-1">
                  Why Gazeal <ArrowUpRight className="w-3 h-3" />
                </a>
                <a href="/#faq" className="text-white/60 hover:text-[#59d862] transition-colors text-sm flex items-center gap-1">
                  FAQ <ArrowUpRight className="w-3 h-3" />
                </a>
                <a href="https://gazeal.co.uk" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#59d862] transition-colors text-sm flex items-center gap-1">
                  Gazeal Main Site <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>

              {/* CTAs */}
              <div className="flex items-center gap-4">
                <Link to="/apply" className="btn-secondary px-5 py-2 text-sm">
                  Apply to Join
                </Link>
                <Link to="/get-paid" className="btn-primary px-5 py-2 text-sm">
                  Get Paid Now
                </Link>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-white/5">
              <p className="text-white/35 text-sm">
                © {new Date().getFullYear()} Gazeal Advance. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="mailto:advance@gazeal.co.uk" className="text-white/35 hover:text-white/60 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" /> advance@gazeal.co.uk
                </a>
                <a href="#" className="text-white/35 hover:text-white/60 text-sm">Privacy</a>
                <a href="#" className="text-white/35 hover:text-white/60 text-sm">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sliding content that reveals the footer */}
      <div 
        className="relative bg-black z-10"
        style={{
          transform: `translateY(-${scrollProgress * 100}%)`,
          transition: 'transform 0.1s linear',
        }}
      >
        {/* This creates the slide-up reveal effect */}
      </div>
    </div>
  );
};

export default FooterReveal;
